import { fromApiGuidesState, ONBOARDING_VERSION } from '../../../shared/onboardingGuides.js'
import { normalizeRole } from '../roles.js'

export { ONBOARDING_VERSION }

const ORIENTATION_PREFIX = 'socialbox_orientation'
const SURFACE_PREFIX = 'socialbox_surface'

const DEFAULT_SNOOZE_DAYS = 7

function orientationKey(userId, role, tenantId = undefined) {
  const canonical = normalizeRole(role, tenantId)
  return `${ORIENTATION_PREFIX}_${ONBOARDING_VERSION}_${userId}_${canonical}`
}

function surfaceKey(userId, surfaceId) {
  return `${SURFACE_PREFIX}_${ONBOARDING_VERSION}_${userId}_${surfaceId}`
}

function storage() {
  if (typeof globalThis === 'undefined') return null
  return globalThis.localStorage ?? null
}

function read(key) {
  const ls = storage()
  if (!ls) return null
  try {
    return ls.getItem(key)
  } catch {
    return null
  }
}

function write(key, value) {
  const ls = storage()
  if (!ls) return
  try {
    ls.setItem(key, value)
  } catch {
    // ignore quota / private mode
  }
}

function remove(key) {
  const ls = storage()
  if (!ls) return
  try {
    ls.removeItem(key)
  } catch {
    // ignore
  }
}

function legacyRoleForOnboardingKey(roleKey) {
  if (roleKey === 'account_manager') return 'admin'
  return roleKey
}

/**
 * Apply server-persisted guide state to localStorage (offline cache).
 * Server state wins on login / profile refresh.
 */
export function hydrateOnboardingGuidesFromServer(apiState, userId, tenantId = undefined) {
  if (!userId) return
  const state = fromApiGuidesState(apiState)
  for (const [roleKey, entry] of Object.entries(state.orientations || {})) {
    const legacyRole = legacyRoleForOnboardingKey(roleKey)
    if (entry?.complete) {
      markOrientationComplete(userId, legacyRole, tenantId)
    } else if (entry?.snoozedUntil) {
      write(orientationKey(userId, legacyRole, tenantId), entry.snoozedUntil)
    }
  }
  for (const surfaceId of state.surfacesSeen || []) {
    markSurfaceSeen(userId, surfaceId)
  }
}

export function isOrientationComplete(userId, role, tenantId = undefined) {
  if (!userId || !role) return true
  return read(orientationKey(userId, role, tenantId)) === 'complete'
}

export function getOrientationSnoozeUntil(userId, role, tenantId = undefined) {
  const raw = read(orientationKey(userId, role, tenantId))
  if (!raw || raw === 'complete') return null
  const parsed = Date.parse(raw)
  return Number.isNaN(parsed) ? null : parsed
}

export function isOrientationSnoozed(userId, role, tenantId = undefined) {
  const until = getOrientationSnoozeUntil(userId, role, tenantId)
  if (!until) return false
  return Date.now() < until
}

export function shouldShowOrientation(userId, role, tenantId = undefined) {
  if (!userId || !role) return false
  if (isOrientationComplete(userId, role, tenantId)) return false
  if (isOrientationSnoozed(userId, role, tenantId)) return false
  return true
}

export function markOrientationComplete(userId, role, tenantId = undefined) {
  if (!userId || !role) return
  write(orientationKey(userId, role, tenantId), 'complete')
}

export function snoozeOrientation(userId, role, days = DEFAULT_SNOOZE_DAYS, tenantId = undefined) {
  if (!userId || !role) return
  const until = Date.now() + days * 24 * 60 * 60 * 1000
  write(orientationKey(userId, role, tenantId), new Date(until).toISOString())
}

export function clearOrientationState(userId, role, tenantId = undefined) {
  if (!userId || !role) return
  remove(orientationKey(userId, role, tenantId))
}

export function isSurfaceSeen(userId, surfaceId) {
  if (!userId || !surfaceId) return true
  return read(surfaceKey(userId, surfaceId)) === 'seen'
}

export function markSurfaceSeen(userId, surfaceId) {
  if (!userId || !surfaceId) return
  write(surfaceKey(userId, surfaceId), 'seen')
}

export function clearSurfaceSeen(userId, surfaceId) {
  if (!userId || !surfaceId) return
  remove(surfaceKey(userId, surfaceId))
}

export function clearAllOnboardingForUser(userId) {
  const ls = storage()
  if (!userId || !ls) return
  try {
    const keys = []
    for (let i = 0; i < ls.length; i += 1) {
      const key = ls.key(i)
      if (
        key &&
        key.includes(userId) &&
        (key.startsWith(ORIENTATION_PREFIX) || key.startsWith(SURFACE_PREFIX))
      ) {
        keys.push(key)
      }
    }
    keys.forEach((key) => ls.removeItem(key))
  } catch {
    // ignore
  }
}
